/**
 * CanvasKeyboard - VR keyboard component for CanvasUI. Renders an interactive virtual keyboard
 * with multiple layouts (letters, numbers, symbols) and shift functionality.
 */

import type * as THREE from 'three';

interface KeyboardButton {
  type: string;
  position: { x: number; y: number };
  width: number;
  height: number;
  padding: number;
  paddingTop: number;
  backgroundColor: string;
  borderRadius: number;
  hover: string;
  fontSize?: number;
  onSelect: () => void;
}

interface KeyboardConfig {
  [key: string]: KeyboardButton | any;
  panelSize: { width: number; height: number };
  height: number;
  body: { backgroundColor: string };
  renderer: THREE.WebGLRenderer;
}

interface KeyboardContent {
  [key: string]: string;
}

export class CanvasKeyboard {
  public keyboard!: any; // CanvasUI instance
  public shift: boolean = false;
  public linkedUI?: any; // CanvasUI instance
  public linkedText?: string;
  public linkedName?: string;
  public linkedElement?: any;

  private language!: string;
  private keyboardIndex!: number;

  constructor(width: number, renderer: THREE.WebGLRenderer, lang: string = 'EN', CanvasUIClass?: any) {
    const config = this.getConfig(lang);
    config.panelSize = { width, height: width * 0.5 };
    config.height = 256;
    config.body = { backgroundColor: '#555' };
    config.renderer = renderer;
    const content = this.getContent(lang);

    if (CanvasUIClass) {
      this.keyboard = new CanvasUIClass(content, config);
      this.keyboard.mesh.visible = false;
    }
  }

  public get mesh(): THREE.Mesh {
    return this.keyboard.mesh;
  }

  public get position(): THREE.Vector3 {
    return this.keyboard.mesh.position;
  }

  public get visible(): boolean {
    return this.keyboard.mesh.visible;
  }

  public set visible(value: boolean) {
    this.keyboard.mesh.visible = value;
  }

  private getConfig(lang: string): KeyboardConfig {
    const config: KeyboardConfig = {
      panelSize: { width: 0, height: 0 },
      height: 0,
      body: { backgroundColor: '' },
      renderer: null as any
    };

    const padding = 10;
    const paddingTop = 20;
    const width = ((512 - 2 * padding) / 10) - padding;
    const height = ((256 - 2 * padding) / 4) - padding;
    const hover = '#333';
    const backgroundColor = '#000';

    // Top row
    let y = padding;
    let x = padding;
    for (let i = 0; i < 10; i++) {
      const btn: KeyboardButton = {
        type: 'button',
        position: { x, y },
        width,
        height,
        padding,
        paddingTop,
        backgroundColor,
        borderRadius: 6,
        hover,
        onSelect: this.onSelect.bind(this, i)
      };
      config[`btn${i}`] = btn;
      x += (width + padding);
    }

    // 2nd row
    y += (height + padding);
    x = padding;
    for (let i = 0; i < 10; i++) {
      const btn: KeyboardButton = {
        type: 'button',
        position: { x, y },
        width,
        height,
        padding,
        paddingTop,
        backgroundColor,
        borderRadius: 6,
        hover,
        onSelect: this.onSelect.bind(this, i + 10)
      };
      config[`btn${i + 10}`] = btn;
      x += (width + padding);
    }

    // 3rd row
    y += (height + padding);
    x = padding;
    for (let i = 0; i < 9; i++) {
      const w = (i === 0 || i === 8) ? (width * 1.5 + padding * 0.5) : width;
      const btn: KeyboardButton = {
        type: 'button',
        position: { x, y },
        width: w,
        height,
        padding,
        paddingTop,
        backgroundColor,
        borderRadius: 6,
        hover,
        onSelect: this.onSelect.bind(this, i + 20)
      };
      config[`btn${i + 20}`] = btn;
      x += (w + padding);
    }

    // 4th row
    y += (height + padding);
    x = padding;
    for (let i = 0; i < 5; i++) {
      const w = (i === 0 || i === 4) ? (width * 2 + padding) : (i === 2) ? (width * 4 + 3 * padding) : width;
      const btn: KeyboardButton = {
        type: 'button',
        position: { x, y },
        width: w,
        height,
        padding,
        paddingTop,
        backgroundColor,
        borderRadius: 6,
        hover,
        onSelect: this.onSelect.bind(this, i + 30)
      };
      if (i === 0) btn.fontSize = 20;
      config[`btn${i + 30}`] = btn;
      x += (w + padding);
    }

    return config;
  }

  private getContent(lang: string, layoutIndex: number = 0): KeyboardContent {
    const content: KeyboardContent = {};
    let keys: string[];

    this.language = lang;
    this.keyboardIndex = layoutIndex;

    switch (layoutIndex) {
      case 0:
        keys = ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p',
          'a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', '@',
          '⇧', 'z', 'x', 'c', 'v', 'b', 'n', 'm', '⇦', '',
          '?123', ',', '   ', '.', '↲'];
        break;
      case 1:
        keys = ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P',
          'A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', '@',
          '⇧', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', '⇦', '',
          '?123', ',', '   ', '.', '↲'];
        break;
      case 2:
        keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0',
          '@', '#', '%', '&', '*', '/', '-', '+', '(', ')',
          '⇧', '?', '!', '"', '\'', '\\', ':', ';', '⇦', '',
          'abc', ',', '   ', '.', '↲'];
        break;
      case 3:
        keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0',
          '€', '£', '$', '^', '=', '|', '{', '}', '[', '}',
          '⇧', '<', '>', '_', '`', '~', ':', ';', '⇦', '',
          'abc', ',', '   ', '.', '↲'];
        break;
      default:
        keys = [];
    }

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (key !== '') content[`btn${i}`] = key;
    }

    return content;
  }

  public setKeyboard(index: number): void {
    this.keyboard.content = this.getContent(this.language, index);
    this.keyboard.needsUpdate = true;
  }

  private onSelect(index: number): void {
    if (!this.visible) return;

    let change = false;

    switch (index) {
      case 34: // Enter
        this.visible = false;
        if (this.linkedElement?.onEnter) this.linkedElement.onEnter(this.linkedText);
        break;
      case 32: // space
        if (this.linkedText !== undefined) {
          this.linkedText += ' ';
          change = true;
        }
        break;
      case 30: // switch keyboard
        this.shift = false;
        if (this.keyboardIndex < 2) {
          this.setKeyboard(2);
        } else {
          this.setKeyboard(0);
        }
        break;
      case 28: // backspace
        if (this.linkedText !== undefined) {
          this.linkedText = this.linkedText.substring(0, this.linkedText.length - 1);
          change = true;
        }
        break;
      case 20: // shift
        this.shift = !this.shift;
        if (this.keyboardIndex === 0) {
          this.setKeyboard(1);
        } else if (this.keyboardIndex === 1) {
          this.setKeyboard(0);
        } else if (this.keyboardIndex === 2) {
          this.setKeyboard(3);
        } else if (this.keyboardIndex === 3) {
          this.setKeyboard(2);
        }
        break;
      default:
        const txt = this.keyboard.content[`btn${index}`];
        if (this.linkedText !== undefined && txt) {
          this.linkedText += txt;
          change = true;
          if (this.keyboardIndex === 1) this.setKeyboard(0);
        }
        break;
    }

    if (change && this.linkedUI && this.linkedName && this.linkedText !== undefined) {
      this.linkedUI.updateElement(this.linkedName, this.linkedText);
      if (this.linkedElement?.onChanged) this.linkedElement.onChanged(this.linkedText);
    }
  }

  public update(): void {
    if (this.keyboard) {
      this.keyboard.update();
    }
  }
}
